import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { 
  User, 
  Receipt, 
  Check, 
  Clock, 
  Store,
  MapPin,
  Phone,
  Users,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CuentaComensalPage() {
  const { codigo } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCuenta();
  }, [codigo]);

  const fetchCuenta = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/public/cuenta/${codigo}`);
      setData(response.data);
    } catch (err) {
      setError({ text: err.response?.data?.detail || 'Cuenta no encontrada', expired: err.response?.status === 410 });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-violet-50 to-white dark:from-violet-950 dark:to-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-violet-500 mb-4" />
          <p className="text-muted-foreground">Cargando tu cuenta...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-50 to-white dark:from-red-950 dark:to-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <Receipt className="w-16 h-16 mx-auto text-red-300 mb-4" />
            <h2 className="text-xl font-bold mb-2" data-testid="cuenta-error-title">{error.expired ? "Link vencido" : "Cuenta no encontrada"}</h2>
            <p className="text-muted-foreground">{error.text}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { business, unit_name, comensal, otros_comensales, session_status } = data;

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 to-white dark:from-violet-950 dark:to-background">
      {/* Header */}
      <div className="bg-violet-600 text-white p-6 pb-16">
        <div className="max-w-md mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <Store className="w-6 h-6" />
            <h1 className="text-xl font-bold">{business?.name}</h1>
          </div>
          {business?.address && (
            <p className="text-violet-200 text-sm flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              {business.address}
            </p>
          )}
          <p className="mt-2 text-violet-200">{unit_name}</p>
        </div>
      </div>

      {/* Main Card */}
      <div className="max-w-md mx-auto px-4 -mt-10">
        <Card className="shadow-xl">
          <CardHeader className="text-center border-b">
            <div className="w-16 h-16 mx-auto rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center mb-3">
              <User className="w-8 h-8 text-violet-600" />
            </div>
            <CardTitle className="text-2xl">{comensal?.nombre}</CardTitle>
            <Badge variant={comensal?.pagado ? "default" : "outline"} className="mt-2">
              {comensal?.pagado ? (
                <><Check className="w-3 h-3 mr-1" /> Pagado</>
              ) : (
                <><Clock className="w-3 h-3 mr-1" /> Pendiente</>
              )}
            </Badge>
          </CardHeader>
          
          <CardContent className="pt-6">
            {/* Items */}
            <div className="space-y-3 mb-6">
              <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                Tu consumo
              </h3>
              {comensal?.items?.length > 0 ? (
                comensal.items.map((item, i) => (
                  <div key={i} className="flex justify-between items-center py-2 border-b last:border-0">
                    <div>
                      <p className="font-medium">{item.nombre}</p>
                      {item.porcentaje < 100 && (
                        <p className="text-sm text-muted-foreground">
                          {item.porcentaje}% del total
                        </p>
                      )}
                    </div>
                    <span className="font-medium">${item.monto?.toLocaleString()}</span>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-center py-4">
                  Aún no hay items asignados
                </p>
              )}
            </div>

            {/* Total */}
            <div className="bg-violet-50 dark:bg-violet-900/20 rounded-lg p-4 mb-6">
              <div className="flex justify-between items-center">
                <span className="text-lg font-medium">Total a pagar</span>
                <span className="text-3xl font-bold text-violet-600">
                  ${comensal?.subtotal?.toLocaleString() || 0}
                </span>
              </div>
            </div>

            {/* Otros comensales */}
            {otros_comensales?.length > 0 && (
              <div className="border-t pt-4">
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Otros en la mesa
                </h3>
                <div className="flex flex-wrap gap-2">
                  {otros_comensales.map((c, i) => (
                    <Badge 
                      key={i} 
                      variant={c.pagado ? "default" : "outline"}
                      className="text-sm"
                    >
                      {c.nombre}
                      {c.pagado && <Check className="w-3 h-3 ml-1" />}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Status de la sesión */}
            {session_status === 'closed' && (
              <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                <Check className="w-6 h-6 mx-auto text-green-600 mb-1" />
                <p className="text-green-700 dark:text-green-400 font-medium">
                  Cuenta cerrada
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-sm text-muted-foreground mt-6 mb-8">
          Código: {comensal?.codigo}
        </p>
      </div>
    </div>
  );
}
